import React, { useEffect, useState } from 'react'

import { LaptopIcon, MoonIcon, SunIcon } from 'lucide-react'

import { SettingsSection } from 'components/display'
import { SegmentedChoice, type SegmentedChoiceOption } from 'components/input'
import { useSnackbar } from 'contexts'
import { useColorMode, useUserProfileMutations, useUserProfileQuery } from 'hooks'
import type { ThemePreference } from 'types'

/**
 * Theme — moved here from Profile, unchanged in behaviour.
 *
 * Note the deliberate contrast with Text size, its neighbour on this page:
 * theme is written to the PROFILE (server), text size to localStorage. That is
 * not an inconsistency. "I prefer dark" should follow the person to whatever
 * machine they sign in on; the right interface scale for a 13" laptop is not
 * the right one for a 27" monitor, so syncing that would fight the user.
 */

const ICON_PROPS = { strokeWidth: 1 } as const

// System is leftmost — it is the default for a fresh user / on login.
const THEME_OPTIONS: readonly SegmentedChoiceOption<ThemePreference>[] = [
  { value: 'system', label: 'System', icon: <LaptopIcon {...ICON_PROPS} /> },
  { value: 'light', label: 'Light', icon: <SunIcon {...ICON_PROPS} /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon {...ICON_PROPS} /> },
]

const errorText = (error: unknown, fallback: string) =>
  error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
    ? error.message
    : fallback

export const ThemeCard = () => {
  const { setMode } = useColorMode()
  const { open: notification } = useSnackbar()
  const { data: profile } = useUserProfileQuery()
  const { updateAsync: updateProfileAsync, isUpdating } = useUserProfileMutations()

  const [selectedTheme, setSelectedTheme] = useState<ThemePreference>('system')

  useEffect(() => {
    if (profile) setSelectedTheme(profile.theme || 'system')
  }, [profile?.id, profile?.theme])

  /**
   * Applied optimistically so the whole app repaints on click, then rolled back
   * if the save fails — a theme that visibly changed and then silently didn't
   * persist is worse than one that snaps back with an explanation.
   */
  const handleChangeTheme = async (newTheme: ThemePreference) => {
    if (!profile || selectedTheme === newTheme) return

    const previousTheme = selectedTheme
    setSelectedTheme(newTheme)
    setMode(newTheme)

    try {
      await updateProfileAsync({ theme: newTheme })
    } catch (error) {
      setSelectedTheme(previousTheme)
      setMode(previousTheme)
      notification({
        type: 'error',
        title: 'Theme Update Failed',
        text: errorText(error, 'Failed to save your theme preference'),
      })
    }
  }

  return (
    <SettingsSection
      title="Theme"
      description={`Choose if Vera's theme should be light or dark, or follow your computer's settings.`}
    >
      <SegmentedChoice
        ariaLabel="Color mode"
        options={THEME_OPTIONS}
        value={selectedTheme}
        onChange={handleChangeTheme}
        disabled={isUpdating}
      />
    </SettingsSection>
  )
}
